import { useMemo } from "react";
import { useGetMany } from "react-admin";
import {
  AutovioCalendarEvent,
  PaidLesson,
  TheoryExam,
  isPaidLesson,
  isTheoryExam,
} from "../model/autovioCalendarEvents";
import { Student } from "../providers/studentsProvider";
import _keyBy from "lodash/keyBy";
import { DateTime } from "luxon";

type EventWithStudent = PaidLesson | TheoryExam;

export function useNegativeBalanceWarnings(events: undefined | AutovioCalendarEvent[]): {
  warningsByDay: Map</* "yyyy-MM-dd" */ string, string>;
  warningsByEventId: Map<string, string>;
} {
  // We only care about future events ...
  const relevantEvents = useMemo(() => {
    const now = DateTime.now();
    return (events ?? []).filter(
      (event) => (isPaidLesson(event) || isTheoryExam(event)) && !event.endedAt && event.start > now,
    ) as Array<EventWithStudent>;
  }, [events]);
  const uniqueStudentIds = useMemo(
    () => Array.from(new Set(relevantEvents.map((event) => event.student.uid))),
    [relevantEvents],
  );
  const drivingSchoolId = relevantEvents[0]?.drivingSchoolId;
  const { data: students } = useGetMany<Student>(
    "students",
    { ids: uniqueStudentIds, meta: { drivingSchoolId } },
    { enabled: !!drivingSchoolId },
  );
  const { warningsByDay, warningsByEventId } = useMemo(() => {
    const studentsById = _keyBy(students, (s) => s.id);
    const warningsCountByDay = new Map<string, number>();
    const warningsByEventId = new Map<string, string>();
    for (const event of relevantEvents) {
      const student = studentsById[event.student.uid];
      if (student && student.balance < 0) {
        const day = event.start.toISODate();
        warningsCountByDay.set(day, (warningsCountByDay.get(day) ?? 0) + 1);
        warningsByEventId.set(event.id, "Fahrschüler hat offene Posten");
      }
    }
    const warningsByDay = new Map<string, string>();
    for (const [day, count] of warningsCountByDay.entries()) {
      warningsByDay.set(
        day,
        count === 1
          ? "1 Termin mit zahlungsrückständigem Fahrschüler"
          : `${count} Termine mit zahlungsrückständigen Fahrschülern`,
      );
    }
    return { warningsByDay, warningsByEventId };
  }, [relevantEvents, students]);
  return { warningsByDay, warningsByEventId };
}
